import { APP_DATA, UI_SETTINGS } from "../common/constants";
import {
  AddCircleIcon,
  CheckIcon,
  CloseIcon,
  DoorIcon,
  HeadphonesIcon,
} from "@100mslive/react-icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  StyledVideoList,
  Text,
  useTheme,
} from "@100mslive/react-ui";
import { useTranslation } from "react-i18next";
import { ROLES, ROLES_CONVERT } from "../common/roles";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  selectAudioTrackVolume,
  selectLocalPeerID,
  useHMSActions,
  useHMSStore,
  useVideoList,
} from "@100mslive/react-sdk";
import {
  useIsHeadless,
  useSetAppDataByKey,
  useUISettings,
} from "./AppData/useUISettings";

import { LiveHodoContext } from "../contexts/LiveHodoContext";
import { Pagination } from "./Pagination";
import ScreenshareTile from "./ScreenshareTile";
import VideoTile from "./VideoTile";
import { convertRolesName } from "../common/utils";
import { useAppConfig } from "./AppData/useAppConfig";
import { useNavigation } from "./hooks/useNavigation";
import { useParams } from "react-router-dom";
import useSortedPeers from "../common/useSortedPeers";

const CabinActions = ({ pagesWithTiles, page, tile }) => {
  const {
    stateCtx: { selectedCabinData, cabins },
    setSelectedCabinData,
  } = useContext(LiveHodoContext);
  const navigate = useNavigation();
  const hmsActions = useHMSActions();
  const currVolume = useHMSStore(selectAudioTrackVolume(tile.peer?.audioTrack));
  const peerRole = tile.peer?.roleName;
  const [selectedCabin, setSelectedCabin] = useState(null);
  const [isPeerMuted, setIsPeerMuted] = useState(false);
  const [isMouseHovered, setIsMouseHovered] = useState(false);
  const [pinnedTrackId, setPinnedTrackId] = useSetAppDataByKey(
    APP_DATA.pinnedTrackId
  );

  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url

  const checkingSelectedCabinData = useCallback(() => {
    if (selectedCabinData?.id) {
      const listPresenterOrPanelist = cabins?.filter(
        peer =>
          peer.roleName === ROLES.PRESENTER || peer.roleName === ROLES.PANELIST
      );
      const listTranslator = cabins?.filter(
        peer =>
          peer.roleName !== ROLES.PRESENTER && peer.roleName !== ROLES.PANELIST
      );
      handleToggleMuteAll(100, listPresenterOrPanelist);
      handleToggleMuteAll(0, listTranslator);
    } else {
      handleToggleMuteAll(0);
    }

    setSelectedCabin(selectedCabinData);
    hmsActions.setVolume(100, selectedCabinData?.audioTrack);
  }, [selectedCabinData, cabins]);

  useEffect(() => {
    checkingSelectedCabinData();
  }, [checkingSelectedCabinData]);

  const handleToggleMuteAll = useCallback(
    (volume = 0, peers) => {
      if (peers?.length > 0) {
        peers?.map(peer => {
          if (peer?.audioTrack) {
            hmsActions.setVolume(volume, peer?.audioTrack);
          }
        });
        return;
      }

      pagesWithTiles[page]?.map(tile => {
        if (tile?.peer?.audioTrack) {
          hmsActions.setVolume(volume, tile?.peer?.audioTrack);
        }
      });
    },
    [pagesWithTiles.length, page]
  );

  const handleSelectedCabin = useCallback(
    peer => {
      handleToggleMuteAll(0);
      hmsActions.setVolume(0, selectedCabinData?.audioTrack);
      setSelectedCabin(peer);

      if (peer?.audioTrack) {
        hmsActions.setVolume(100, peer?.audioTrack);
        let cabinURL = `/cabin/${urlRoomId}`;
        if (userRole) {
          cabinURL += `/${userRole}`;
        }
        setPinnedTrackId(peer?.audioTrack || peer?.videoTrack);
        setSelectedCabinData(peer);
        navigate(cabinURL);
      }
    },
    [handleToggleMuteAll, selectedCabinData]
  );

  const handleClearCabin = useCallback(() => {
    setSelectedCabin(null);
    setIsPeerMuted(false);
    handleToggleMuteAll(0);
    setSelectedCabinData(null);
    checkingSelectedCabinData();
  }, [handleToggleMuteAll, checkingSelectedCabinData]);

  const handleMutePeer = useCallback(
    peer => {
      if (peer?.audioTrack) {
        hmsActions.setVolume(
          isPeerMuted && currVolume === 0 ? 100 : 0,
          peer?.audioTrack
        );
        setIsPeerMuted(!isPeerMuted);
      }
    },
    [currVolume]
  );
  const { t } = useTranslation();
  const onHoverHandler = useCallback(event => {
    setIsMouseHovered(event.type === "mouseenter");
  }, []);

  return (
    <Box
      css={{
        position: "absolute",
        top: "$6",
        left: "$6",
        marginLeft: "$2",
        marginTop: "$2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
      onMouseEnter={onHoverHandler}
      onMouseLeave={onHoverHandler}
    >
      <Flex
        className="cabin-action"
        direction="column"
        css={{
          width: "36px",
          height: "36px",
          borderRadius: "$round",
          cursor: "pointer",
        }}
        // variant={
        //   selectedCabin && tile.peer?.id === selectedCabin?.id
        //     ? "danger"
        //     : "primary"
        // }
        onClick={() =>
          (selectedCabin && tile.peer?.id === selectedCabin?.id) ||
          (selectedCabin && tile.peer?.roleName === selectedCabin?.roleName)
            ? handleClearCabin()
            : handleSelectedCabin(tile.peer)
        }
        disabled={selectedCabin && tile.peer?.id !== selectedCabin?.id}
      >
        {(selectedCabin && tile.peer?.id === selectedCabin?.id) ||
        (selectedCabin && tile.peer?.roleName === selectedCabin?.roleName) ? (
          <Box css={{ margin: "auto" }}>
            <CloseIcon />
          </Box>
        ) : (
          <Box css={{ margin: "auto" }}>
            <HeadphonesIcon />
          </Box>
        )}
      </Flex>
      {isMouseHovered && (
        <Text
          css={{
            padding: "$1",
            fontSize: "12px",
          }}
        >
          {(selectedCabin && tile.peer?.id === selectedCabin?.id) ||
          (selectedCabin && tile.peer?.roleName === selectedCabin?.roleName)
            ? "Rời cabin"
            : `Nghe ${t("obj_roles." + ROLES_CONVERT[convertRolesName(peerRole)])}`}
        </Text>
      )}
    </Box>
  );
};

const List = ({
  maxTileCount,
  peers,
  maxColCount,
  maxRowCount,
  includeScreenShareForPeer,
  isCabin,
}) => {
  const { aspectRatio } = useTheme();
  const tileOffset = useAppConfig("headlessConfig", "tileOffset");
  const isHeadless = useIsHeadless();
  const hideLocalVideo = useUISettings(UI_SETTINGS.hideLocalVideo);
  const localPeerId = useHMSStore(selectLocalPeerID);
  let sortedPeers = useSortedPeers({ peers, maxTileCount });
  const [tilesScreenShare, setTilesScreenShare] = useState([]);

  if (hideLocalVideo && sortedPeers.length > 1) {
    sortedPeers = filterPeerId(sortedPeers, localPeerId);
  }
  const { ref, pagesWithTiles } = useVideoList({
    peers: sortedPeers,
    maxTileCount,
    maxColCount,
    maxRowCount,
    includeScreenShareForPeer,
    aspectRatio,
    offsetY: getOffset({ isHeadless, tileOffset }),
  });
  const [page, setPage] = useState(0);

  useEffect(() => {
    // currentPageIndex should not exceed pages length
    if (page >= pagesWithTiles.length) {
      setPage(0);
    }

    let tiles = [];
    pagesWithTiles.forEach(item => {
      const data = item.filter(tile => tile.track?.source === "screen");
      if (data.length > 0) {
        tiles.push(data);
      }
    });

    setTilesScreenShare(tiles);
  }, [pagesWithTiles.length, sortedPeers, page]);

  return (
    <StyledVideoList.Root ref={ref}>
      <StyledVideoList.Container
        css={{ flexWrap: "wrap", placeContent: "center" }}
      >
        {tilesScreenShare && tilesScreenShare.length > 0
          ? tilesScreenShare[page]?.map((tile, index) => {
              if (tile.width === 0 || tile.height === 0) {
                return null;
              }
              return (
                <Fragment key={tile.track?.id || tile.peer.id}>
                  {tile.track?.source === "screen" ? (
                    <ScreenshareTile
                      isSmallTile={true}
                      width={tile.width}
                      height={tile.height}
                      peerId={tile.peer.id}
                    />
                  ) : (
                    <Box css={{ position: "relative" }}>
                      <VideoTile
                        width={tile.width}
                        height={tile.height}
                        peerId={tile.peer?.id}
                        trackId={tile.track?.id}
                      />
                      {/* {isCabin ? (
                        <CabinActions
                          tile={tile}
                          pagesWithTiles={tilesScreenShare}
                          page={page}
                        />
                      ) : (
                        ""
                      )} */}
                    </Box>
                  )}
                </Fragment>
              );
            })
          : null}
      </StyledVideoList.Container>
      {!isHeadless && tilesScreenShare.length > 1 ? (
        <Pagination
          page={page}
          setPage={setPage}
          numPages={tilesScreenShare.length}
        />
      ) : null}
    </StyledVideoList.Root>
  );
};

const VideoScreenShareList = React.memo(List);

/**
 * returns a new array of peers with the peer with peerId removed,
 * keeps the reference same if peer is not found
 */
function filterPeerId(peers, peerId) {
  const oldPeers = peers; // to keep the reference same if peer is not found
  let foundPeerToFilterOut = false;
  peers = [];
  for (let i = 0; i < oldPeers.length; i++) {
    if (oldPeers[i].id === peerId) {
      foundPeerToFilterOut = true;
    } else {
      peers.push(oldPeers[i]);
    }
  }
  if (!foundPeerToFilterOut) {
    peers = oldPeers;
  }
  return peers;
}

const getOffset = ({ tileOffset, isHeadless }) => {
  if (!isHeadless || isNaN(Number(tileOffset))) {
    return 32;
  }
  return Number(tileOffset);
};

export default VideoScreenShareList;
